import { FC } from 'react';
import ReactGA4 from 'react-ga4';
import { Tr } from '@utils';
import { AdditionalService, PackageDealCalculated } from '@services';
import { Grid, Link, List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PackageDealAndServiceLogoViewer } from '@components/portal/package-deals';

interface IServiceDisplayProps {
	service: AdditionalService;
	packageDeal: PackageDealCalculated;
	onMoreClick?: (service: AdditionalService) => void;
}

const ServiceDisplay: FC<IServiceDisplayProps> = ({ service, packageDeal, onMoreClick }) => {
	return (
		<Stack direction="row" spacing={1}>
			<PackageDealAndServiceLogoViewer src={service.logoUrl || ''} containerProps={{ alignItems: 'start' }} />
			<Stack direction="column" spacing={0.5}>
				<Typography sx={{ fontWeight: 600 }}>{service.name}</Typography>
				{service.summary ? <Typography>{service.summary}</Typography> : null}
				{service.description ? (
					<Link
						className="electrification-wizard-option-learn-more" // GTM tag trigger
						sx={{ fontWeight: 500, cursor: 'pointer', paddingTop: 0.5 }}
						onClick={() => {
							ReactGA4.event({
								category: 'Electrification Wizard',
								action: 'Electrification Wizard - Learn More Click',
								label: `PackageId_${packageDeal.packageDealId};ServiceId_${service.id};ServiceName_${service.name}`
							});

							onMoreClick?.(service);
						}}
					>
						<Tr.Portal path="wizard.learn-more" />
					</Link>
				) : null}
			</Stack>
		</Stack>
	);
};

export interface IWizardPackageOptionCardContentProps {
	type: 'recommended' | 'alternative';
	packageDeal?: PackageDealCalculated;
	refPackageDeal?: PackageDealCalculated;
	onViewingServiceChanged?: (service: AdditionalService) => void;
}

export const WizardPackageOptionCardContent: FC<IWizardPackageOptionCardContentProps> = ({
	type,
	packageDeal,
	refPackageDeal,
	onViewingServiceChanged
}) => {
	if (type === 'recommended') {
		return (
			<Grid container spacing={3}>
				{packageDeal?.packageDeal?.services.map((service) => (
					<Grid item xs={6} key={`package-${packageDeal.id}-service-${service.id}`}>
						<ServiceDisplay service={service} packageDeal={packageDeal} onMoreClick={onViewingServiceChanged} />
					</Grid>
				))}
			</Grid>
		);
	} else {
		const commonServices = (refPackageDeal?.packageDeal?.services || []).filter(
			({ id }) => !!(packageDeal?.packageDeal?.services || []).find((service) => service.id === id)
		);
		const notAvailableServices = (refPackageDeal?.packageDeal?.services || []).filter(
			({ id }) => !commonServices.find((service) => service.id === id)
		);
		return packageDeal ? (
			<Stack direction="column" spacing={3}>
				{commonServices.length ? (
					<Stack direction="column" spacing={2}>
						{commonServices.map((service) => (
							<ServiceDisplay
								key={`package-${packageDeal?.id}-service-${service.id}`}
								service={service}
								packageDeal={packageDeal}
								onMoreClick={onViewingServiceChanged}
							/>
						))}
					</Stack>
				) : null}
				{notAvailableServices.length ? (
					<Stack direction="column" spacing={2}>
						<List dense disablePadding>
							{notAvailableServices.map((service) => (
								<ListItem key={`not-available-service-${service.id}`} disableGutters>
									<ListItemIcon sx={{ minWidth: 0, marginRight: 1 }}>
										<CloseIcon sx={{ width: 28, height: 28, color: 'grey.800' }} />
									</ListItemIcon>
									<ListItemText
										primary={
											<Tr.Portal path="wizard.not-available-service-text" options={{ serviceName: service.name }} />
										}
										primaryTypographyProps={{ variant: 'body1' }}
									/>
								</ListItem>
							))}
						</List>
					</Stack>
				) : null}
			</Stack>
		) : null;
	}
};
